import { useTranslation } from 'react-i18next';
import { Printer } from 'lucide-react';
import { formatIqd, fromMillidinars, type OrderDto } from '@gc/shared';

interface OrderTicketsDialogProps {
  order: OrderDto;
  currencySymbol: string;
  onClose: () => void;
}

/**
 * What each station was sent (SPEC §5.4, §10).
 *
 * These are the queued tickets read back from the server, not a client-side
 * re-render of the cart: a matcha and a burger in one order appear here as two
 * tickets, which is the staff member's confirmation that the split happened.
 *
 * The jobs are QUEUED. Actually driving the printer is the Print Bridge's job,
 * so this screen deliberately claims nothing about paper having come out.
 */
export function OrderTicketsDialog({ order, currencySymbol, onClose }: OrderTicketsDialogProps) {
  const { t } = useTranslation();

  const destinationLabel =
    order.destinationName ?? order.tabName ?? t(`cafe.destinationType.${order.destinationType}`);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div className="max-h-full w-full max-w-2xl overflow-auto rounded-card border border-subtle bg-raised p-5 shadow-2xl">
        <header className="mb-4">
          <h2 className="text-lg font-semibold text-content">{t('cafe.orderSent')}</h2>
          <p className="mt-1 text-sm text-content-secondary">
            {t('cafe.orderSentDetail', {
              destination: destinationLabel,
              total: formatIqd(fromMillidinars(order.totalMd), { currencySymbol }),
            })}
            {order.invoiceNumber ? ` · ${order.invoiceNumber}` : ''}
          </p>
        </header>

        {order.tickets.length === 0 ? (
          <p className="text-sm text-content-muted">{t('cafe.noTickets')}</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {order.tickets.map((ticket, index) => (
              <article
                key={`${ticket.printerId}-${index}`}
                className="rounded-control border border-subtle bg-input p-3"
              >
                <header className="mb-2 flex items-center gap-2 border-b border-subtle pb-2">
                  <Printer className="h-4 w-4 text-content-muted" aria-hidden />
                  <span className="font-semibold text-content">{ticket.printerName}</span>
                  <span className="ms-auto rounded-full bg-hover px-2 py-0.5 text-xs text-content-secondary">
                    {t('cafe.queued')}
                  </span>
                </header>

                <dl className="mb-2 space-y-0.5 text-xs text-content-secondary">
                  <div className="flex gap-2">
                    <dt>{t('cafe.destination')}:</dt>
                    <dd className="text-content">
                      {ticket.destination.name ??
                        t(`cafe.destinationType.${ticket.destination.type}`)}
                    </dd>
                  </div>
                  <div className="flex gap-2">
                    <dt>{t('cafe.tabName')}:</dt>
                    <dd className="text-content">{ticket.tabName}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt>{t('cafe.time')}:</dt>
                    <dd className="tabular text-content">
                      {new Date(ticket.placedAt).toLocaleTimeString()}
                    </dd>
                  </div>
                </dl>

                <ul className="space-y-1">
                  {ticket.items.map((item) => (
                    <li key={item.orderItemId} className="flex gap-2 text-sm text-content">
                      <span className="tabular w-6 shrink-0 font-semibold">{item.quantity}×</span>
                      <span className="min-w-0 flex-1">
                        {item.name}
                        {item.note ? (
                          <span className="block text-xs text-content-muted">{item.note}</span>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>

                {ticket.note ? (
                  <p className="mt-2 border-t border-subtle pt-2 text-xs text-content-secondary">
                    {ticket.note}
                  </p>
                ) : null}
              </article>
            ))}
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover"
          >
            {t('common.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
